import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, SectionTitle, Note, Image, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider, Separator, Result, CorrectionBox } from '../../../../components/course/CourseComponents';


const Chapitre3Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Ajout du corps du serpent qui suit la tête + gestion de la taille du serpent
        </p>
      </InfoBox>

      <Separator type="text" text="Création du corps" color="gray-darker" size="md" />
      <SectionTitle>🎯 1/ Création de la liste et affichage du corps</SectionTitle>

      <p>Pour afficher le corps du serpent, on va utiliser une liste qui stocke toutes les coordonnées de la tête au fil du temps, à chaque frame.</p>

      <p>D'abord, on crée la liste <InlineCode>corps</InlineCode> en global :</p>

      <CodeBlock language="javascript">
{`let corps = [];`}
      </CodeBlock>

      <p>Ensuite, dans <InlineCode>draw</InlineCode>, juste après avoir calculé le déplacement, on ajoute chaque nouvelle coordonnée de la tête (posX, posY) :</p>

      <CodeBlock language="javascript">
{`corps.push({ x: posX, y: posY });`}
      </CodeBlock>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>liste.push(élément)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Ajoute un élément à notre liste.</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>liste.shift()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Supprime le premier élément de la liste.</td>
            </tr>
                         <tr>
               <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>liste.length</InlineCode></td>
               <td className="border border-gray-300 dark:border-gray-600 p-2">Nombre d'éléments dans notre liste.</td>
             </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>La fonction <InlineCode>push</InlineCode> permet d'ajouter un élément à la fin de la liste <InlineCode>corps</InlineCode>.</p>
      <p>Cet élément est un objet contenant deux variables : <InlineCode>x</InlineCode> et <InlineCode>y</InlineCode>, qui sont les coordonnées de la tête du serpent.</p>

      <p>Enfin, pour chaque point de la liste, on affiche l'image du corps.</p>
      <p>On parcourt la liste avec une boucle <InlineCode>for</InlineCode>, on récupère l'élément courant à l'index <InlineCode>i</InlineCode>, puis on utilise <InlineCode>x</InlineCode> et <InlineCode>y</InlineCode> pour afficher l'image du corps :</p>

      <CodeBlock language="javascript">
{`for (let i = 0; i < corps.length; i = i + 1) {
  let p = corps[i];
  image(imageCorps, p.x, p.y);
}`}
      </CodeBlock>

              <Note type="exercice" title="🧪 À toi de jouer : Affichage du corps">
          <p>- Crée une liste globale qu'on appellera <InlineCode>corps</InlineCode> :</p>

          <CodeBlock language="javascript">
{`let corps = [];`}
          </CodeBlock>

          <p>- Dans <InlineCode>draw</InlineCode>, après avoir calculé le déplacement de la tête, ajoute les coordonnées <InlineCode>posX</InlineCode> et <InlineCode>posY</InlineCode> dans la liste :</p>

          <CodeBlock language="javascript">
{`corps.push({ x: posX, y: posY });`}
          </CodeBlock>

          <p>- Enfin, avant d'afficher la tête, parcours la liste <InlineCode>corps</InlineCode> et affiche l'image du corps pour chaque élément :</p>

          <CodeBlock language="javascript">
{`// Affichage corps
for (let i = 0; i < corps.length; i = i + 4) {
  let p = corps[i];
  image(imageCorps, p.x, p.y);
}`}
          </CodeBlock>

        <br/>
        <Result>Le corps du serpent s'allonge progressivement en suivant la tête.</Result>

        <CorrectionBox title="Correction :">
          <CodeBlock language="javascript">
{`function draw() {
  imageMode(CORNER);
  background(imageFond);
  imageMode(CENTER);

  // Déplacement
  let dt = deltaTime / 1000;
  posX = posX + dx * vitesse * dt;
  posY = posY + dy * vitesse * dt;
  corps.push({ x: posX, y: posY });

  // Affichage corps
  for (let i = 0; i < corps.length; i = i + 4) {
    let p = corps[i];
    image(imageCorps, p.x, p.y);
  }

  // Affichage et animation de la tête
  let imageTete;
  if (frameCount % 100 > 10) imageTete = imageTete1;
  else imageTete = imageTete2;
  image(imageTete, posX, posY);
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Note type="tip" title="💡 Pourquoi afficher les images du corps avant l'image de la tête ?">
        <p>L'ordre d'affichage des images détermine leur superposition : la dernière image affichée sera toujours au-dessus de celles d'avant.</p>
        <p>Ainsi, tu es sûr que la tête s'affiche devant le corps, et pas derrière.</p>
        <p>Si tu fais l'inverse, la tête sera cachée sous le corps, ce qui n'est pas esthétique.</p>
      </Note>

      <Separator type="text" text="Performance et esthétique" color="gray-darker" size="md" />
      <SectionTitle>⚡ 2/ Performance et amélioration esthétique</SectionTitle>

      <p>Afin d'améliorer les performances et l'aspect visuel, on ne va pas afficher une image corps à chaque point de la liste : c'est beaucoup trop, et on ne voit même plus les images tellement elles se superposent.</p>

      <p>On va plutôt en afficher tous les 4 points.</p>
      <p>Pour cela, on ne parcourt plus la liste un par un (<InlineCode>i = i + 1</InlineCode>), mais de 4 en 4 (<InlineCode>i = i + 4</InlineCode>) :</p>

      <CodeBlock language="javascript">
{`for (let i = 0; i < corps.length; i = i + 4) {
  let p = corps[i];
  image(imageCorps, p.x, p.y);
}`}
      </CodeBlock>

      <p>Ainsi, on affiche moins d'images, et le corps devient plus visible.</p>

      <Note type="exercice" title="🧪 À toi de jouer : afficher le corps avec moins d'images">
        <p>Dans <InlineCode>draw</InlineCode>, parcours la liste de 4 en 4 :</p>

        <CodeBlock language="javascript">
{`for (let i = 0; i < corps.length; i = i + 4) {
  let p = corps[i];
  image(imageCorps, p.x, p.y);
}`}
        </CodeBlock>

        <Result>Tu devrais avoir moins d'images et mieux voir le corps du serpent.</Result>
      </Note>

      <Note type="tip" title="🔧 Essaye d'autres valeurs comme i = i + 8 ou i = i + 10.">
        <p>Trouve celle qui te convient le mieux.</p>
        <p>Moi, j'aime bien i = i + 4, car on voit environ la moitié de l'image corps à chaque fois.</p>
      </Note>

      <Separator type="text" text="Taille du serpent" color="gray-darker" size="md" />
      <SectionTitle>📏 3/ Limiter la taille du serpent</SectionTitle>

      <p>On aimerait que la taille du serpent soit fixe.</p>
      <p>Pour cela, rien de plus simple : il suffit de limiter la taille de la liste <InlineCode>corps</InlineCode>, car c'est elle qui définit la longueur du serpent.</p>

      <p>Actuellement, on ajoute un point par frame, sans jamais rien supprimer : le corps ne fait que grandir.</p>
      <p>Il suffit donc de supprimer les éléments en trop pour que la taille du corps reste constante.</p>

      <p>D'abord, on crée une variable globale <InlineCode>longueur</InlineCode> :</p>

      <CodeBlock language="javascript">
{`let longueur = 16; // longueur initiale du snake`}
      </CodeBlock>

      <p>Puis dans <InlineCode>draw</InlineCode>, après avoir ajouté les nouvelles coordonnées avec <InlineCode>push</InlineCode>, on supprime les éléments en trop.</p>

      <Note type="tip" title="🤔 On supprime au début ou à la fin de la liste ?">
        <p>Puisqu'on utilise <InlineCode>push</InlineCode> pour ajouter les nouvelles coordonnées à la fin de la liste, les points les plus anciens sont au début de la liste.</p>
        <p>Il faut donc supprimer les premiers éléments.</p>
      </Note>

      <p>On utilise la fonction JavaScript <InlineCode>shift()</InlineCode>, qui permet de supprimer le premier élément d'une liste.</p>

      <p>Tant que la longueur de la liste est supérieure à <InlineCode>longueur</InlineCode>, on supprime le premier élément.</p>
      <p>Voila ce que ça donne en code, avec une boucle <InlineCode>while</InlineCode> :</p>

      <CodeBlock language="javascript">
{`while (corps.length > longueur) {
  corps.shift();
}`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : Limiter la taille du serpent">
        <p>- Crée une variable globale <InlineCode>longueur</InlineCode> :</p>

        <CodeBlock language="javascript">
{`let longueur = 16;`}
        </CodeBlock>

        <p>- Dans <InlineCode>draw</InlineCode>, après le <InlineCode>push</InlineCode>, supprime les éléments en trop :</p>

        <CodeBlock language="javascript">
{`while (corps.length > longueur) {
  corps.shift();
}`}
        </CodeBlock>

        <Result>Le serpent a maintenant une taille constante.</Result>
      </Note>

      <Note type="tip" title="🔧 Change la valeur de longueur">
        <p>Change la valeur initiale de <InlineCode>longueur</InlineCode> pour voir si la taille du serpent change.</p>
        <p>Essaye 100 par exemple.</p>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Serpent avec corps</SectionTitle>


      <CodeBlock language="javascript">
{`// Jeu 4 : Snake
let imageFond;
let imageTete1;
let imageTete2;
let imageTeteMort;
let imageCorps;
let imagePomme;
let sonMort;
let sonCroque

let vitesse = 270; // vitesse du snake en pixels par seconde
let longueur = 16; // longueur initiale du snake

let posX, posY;
let dx = 1, dy = 0;
let corps = [];

//--------------------------------- PRELOAD ------------------------------------------
function preload(){
  imageFond = loadImage('fond.png')
  imageTete1 = loadImage('snake-tete1.png');
  imageTete2 = loadImage('snake-tete2.png');
  imageTeteMort = loadImage('snake-tete-mort.png');
  imageCorps = loadImage('snake-corps.png');
  imagePomme = loadImage('pomme.png');
  sonMort = loadSound('mort.wav')
  sonCroque = loadSound('croque.wav')
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
  posX = 40;
  posY = height / 2;
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
  imageMode(CENTER);

  // Déplacement
  let dt = deltaTime / 1000;
  posX = posX + dx * vitesse * dt;
  posY = posY + dy * vitesse * dt;
  corps.push({ x: posX, y: posY });

  // Taille limite
  while (corps.length > longueur) {
    corps.shift();
  }

  // Affichage corps
  for (let i = 0; i < corps.length; i = i + 4) {
    let p = corps[i];
    image(imageCorps, p.x, p.y);
  }

  // Affichage et animation de la tête
  let imageTete;
  if (frameCount % 100 > 10)  imageTete = imageTete1;
  else imageTete = imageTete2;
  image(imageTete, posX, posY);
}

//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {
  if (keyCode === UP_ARROW && dy !== 1) { dx = 0; dy = -1; }
  else if (keyCode === DOWN_ARROW && dy !== -1) { dx = 0; dy = 1; }
  else if (keyCode === LEFT_ARROW && dx !== 1) { dx = -1; dy = 0; }
  else if (keyCode === RIGHT_ARROW && dx !== -1) { dx = 1; dy = 0; }
}`}
      </CodeBlock>

      <p>🎯 Bravo ! Dans le prochain chapitre, nous allons ajouter une pomme que le serpent pourra manger pour grandir !</p>
    </>
  );
};

export default Chapitre3Content; 

